import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import { ComponentPropsWithoutRef, ReactNode, useEffect, useRef } from 'react';

type CommandProps = ComponentPropsWithoutRef<typeof CommandPrimitive>;

export function Command({ className = '', ...props }: CommandProps) {
  return (
    <CommandPrimitive
      className={`flex h-full w-full flex-col overflow-hidden rounded-lg bg-white text-alba-black-500 ${className}`}
      loop
      {...props}
    />
  );
}

export type CommandDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void; // eslint-disable-line
  children: ReactNode;
};

export const CommandDialog = ({
  open,
  onOpenChange,
  children,
}: CommandDialogProps) => {
  useEffect(() => {
    if (open) {
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className='fixed inset-0 z-50 bg-black/50'
        onClick={() => onOpenChange(false)}
      />
      <div
        className='fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2'
        role='dialog'
        aria-modal='true'
      >
        <Command
          className='shadow-lg border border-alba-gray-200'
          shouldFilter
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onOpenChange(false);
            }
          }}
        >
          {children}
        </Command>
      </div>
    </>
  );
};

export type CommandInputProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Input
>;

export function CommandInput({ className = '', ...props }: CommandInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the input when mounted with a slight delay
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className='flex items-center border-b border-alba-gray-200 px-4'>
      <Search
        className='mr-2 h-5 w-5 shrink-0 text-alba-gray-500'
        strokeWidth={1.6}
      />
      <CommandPrimitive.Input
        ref={inputRef}
        className={`flex h-12 w-full rounded-md bg-transparent py-3 text-base sm:text-sm outline-none placeholder:text-alba-gray-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        autoComplete='off'
        autoCorrect='off'
        spellCheck={false}
        {...props}
      />
    </div>
  );
}

export type CommandListProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.List
>;

export function CommandList({ className = '', ...props }: CommandListProps) {
  return (
    <CommandPrimitive.List
      className={`max-h-[400px] overflow-y-auto overflow-x-hidden p-2 ${className}`}
      {...props}
    />
  );
}

export type CommandEmptyProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Empty
>;

export function CommandEmpty(props: CommandEmptyProps) {
  return (
    <CommandPrimitive.Empty
      className='py-6 text-center text-sm text-alba-gray-500'
      {...props}
    />
  );
}

export type CommandGroupProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Group
>;

export function CommandGroup({ className = '', ...props }: CommandGroupProps) {
  return (
    <CommandPrimitive.Group
      className={`overflow-hidden px-2 py-2 text-alba-black-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-alba-gray-600 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide ${className}`}
      {...props}
    />
  );
}

export type CommandSeparatorProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Separator
>;

export function CommandSeparator({
  className = '',
  ...props
}: CommandSeparatorProps) {
  return (
    <CommandPrimitive.Separator
      className={`-mx-1 h-px bg-alba-gray-200 my-2 ${className}`}
      {...props}
    />
  );
}

export type CommandItemProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Item
>;

export function CommandItem({ className = '', ...props }: CommandItemProps) {
  return (
    <CommandPrimitive.Item
      className={`relative flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-gray-100 data-[selected=true]:text-alba-blue-700 data-[disabled=true]:opacity-50 hover:bg-alba-gray-50 transition-colors ${className}`}
      {...props}
    />
  );
}

type CommandShortcutProps = ComponentPropsWithoutRef<'span'>;

export function CommandShortcut({
  className = '',
  ...props
}: CommandShortcutProps) {
  return (
    <span
      className={`ml-auto text-xs tracking-widest text-alba-gray-500 ${className}`}
      {...props}
    />
  );
}
